import { useTheme } from '@shopify/restyle';
import { Map } from 'app/design/primitives/map';

import type { Address } from 'app/types/address';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { Platform } from 'react-native';
import type { Details, LatLng, Region } from 'react-native-maps';
import { getDistanceBetweenCoords } from 'app/utils/helpers';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import type { Theme } from 'app/shopify';

/** Approximate meters per degree of latitude, used to size the map region so the radius circle fits. */
const METERS_PER_DEGREE_LATITUDE = 111_320;
/** Minimum cos(latitude) used to avoid an unbounded longitude delta near the poles. */
const MIN_COS_LATITUDE = 0.1;
/** Extra margin around the radius circle so it stays fully visible in its entirety. */
const CIRCLE_VIEWPORT_PADDING = 1.25;

interface RadiusProps extends Omit<ComponentProps<typeof Map>, 'region'> {
  address: Pick<Address, 'latitude' | 'longitude' | 'radius'> & {
    radiusCenterLatitude?: Address['radiusCenterLatitude'];
    radiusCenterLongitude?: Address['radiusCenterLongitude'];
  };
  onCenterChanged: (center: LatLng) => void;
  noteBelowMap?: string;
}

export function Radius({ address, onCenterChanged, noteBelowMap, ...rest }: RadiusProps) {
  const theme = useTheme<Theme>();

  const { radius, ...point } = address;

  const initialRegion = {
    latitude: point.radiusCenterLatitude ?? point.latitude,
    longitude: point.radiusCenterLongitude ?? point.longitude,
  };
  const addressKey = [
    address.latitude,
    address.longitude,
    address.radius,
    address.radiusCenterLatitude,
    address.radiusCenterLongitude,
  ].join(':');
  const [mapState, setMapState] = useState({ addressKey, center: initialRegion, isSaving: false });
  let currentMapState = mapState;
  if (mapState.addressKey !== addressKey) {
    const receivedOldAddress =
      mapState.isSaving &&
      (!address.radiusCenterLatitude || Math.abs(address.radiusCenterLatitude - mapState.center.latitude) > 0.0001);
    currentMapState = receivedOldAddress
      ? { ...mapState, addressKey }
      : { addressKey, center: initialRegion, isSaving: false };
    setMapState(currentMapState);
  }
  const { center } = currentMapState;

  // Keep the circle at the center of the map
  const onRegionChange = (region: Region) => {
    if (getDistanceBetweenCoords(region.latitude, region.longitude, point.latitude, point.longitude) > radius) {
      // Don't allow the circle to go farther than the radius, to keep the marker inside the circle
      return;
    }
    setMapState((current) => ({
      ...current,
      center: {
        latitude: Math.round(region.latitude * 100000) / 100000,
        longitude: Math.round(region.longitude * 100000) / 100000,
      },
    }));
  };

  // On drag ended, save the new center
  const onRegionChangeComplete = (region: Region, details: Details) => {
    if (details.isGesture === false) return;
    setMapState((current) => ({ ...current, isSaving: true }));
    onCenterChanged(center);
  };

  return (
    <Column>
      <Map
        {...rest}
        {...(Platform.OS === 'ios' && { mapType: 'mutedStandard' })}
        loadingBackgroundColor={theme.colors['surface.disabled']}
        onRegionChange={onRegionChange}
        onRegionChangeComplete={onRegionChangeComplete}
        region={{
          ...initialRegion,
          /**
           * Zoom level that keeps the radius circle fully visible in its
           * entirety: the viewport is at least as large as the circle's
           * diameter in both axes, accounting for latitude compression.
           */
          latitudeDelta: (CIRCLE_VIEWPORT_PADDING * 2 * radius) / METERS_PER_DEGREE_LATITUDE,
          longitudeDelta:
            (CIRCLE_VIEWPORT_PADDING * 2 * radius) /
            (METERS_PER_DEGREE_LATITUDE *
              Math.max(Math.cos((initialRegion.latitude * Math.PI) / 180), MIN_COS_LATITUDE)),
        }}
        rotateEnabled={false}
        style={{
          backgroundColor: theme.colors['surface.disabled'],
          borderColor: theme.colors['surface.inverse.tertiary-neutral'],
          borderRadius: theme.borderRadii['radius.lg'],
          borderWidth: 1,
          overflow: 'hidden',
        }}
      >
        <Map.Marker coordinate={point} />
        <Map.Circle center={center} radius={radius} />
      </Map>
      {noteBelowMap ? (
        <Text alignSelf="center" paddingTop="spacing.general.x4" variant="bodyBase">
          {noteBelowMap}
        </Text>
      ) : null}
    </Column>
  );
}
